import React, { useState, useRef, useEffect } from 'react';
import './SearchableSelect.css';

const SearchableSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select...', 
  getOptionLabel = (option) => option.label || option.name || String(option),
  getOptionValue = (option) => option.value || option.id || option,
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Find the selected option
  const selectedOption = options.find(opt => {
    const optValue = getOptionValue(opt);
    const currentValue = value === null || value === undefined ? '' : value;
    return String(optValue) === String(currentValue);
  });

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    if (!searchTerm.trim()) return true;
    const label = getOptionLabel(option).toLowerCase();
    const search = searchTerm.toLowerCase();
    return label.includes(search);
  });

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle option selection
  const handleSelectOption = (option) => {
    const optValue = getOptionValue(option);
    onChange(optValue);
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  // Handle input blur (with delay to allow click events)
  const handleBlur = () => {
    // Delay to allow click events on options
    setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
    }, 200);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const displayValue = selectedOption ? getOptionLabel(selectedOption) : '';
  const inputValue = isOpen ? searchTerm : displayValue;

  return (
    <div className={`searchable-select ${className}`} ref={containerRef}>
      <div className="searchable-select-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="searchable-select-input"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        <span className="searchable-select-arrow">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown" ref={listRef}>
          {filteredOptions.length === 0 ? (
            <div className="searchable-select-no-results">
              No results found
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const optValue = getOptionValue(option);
              const currentValue = value === null || value === undefined ? '' : value;
              const isSelected = String(optValue) === String(currentValue);
              const isHighlighted = index === highlightedIndex;
              
              return (
                <div
                  key={optValue}
                  className={`searchable-select-option ${
                    isSelected ? 'selected' : ''
                  } ${isHighlighted ? 'highlighted' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {getOptionLabel(option)}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;

